import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { AppProps } from 'next/app'

import { initializeApollo } from '../../../lib/apolloClient'
import globalCache from '../../../lib/globalCache'

import JournalDetailPost from '../../../components/journal/JournalDetailPost/JournalDetailPost'
import {
  journalItem,
  journalItemVariables,
  journalItem_element,
} from '../../../__generated__/journalItem'
import { JOURNAL_ITEM, JOURNAL_ITEMS } from '../../../queries/journalQuery'
import ArticleProvider from '../../../contexts/articleContext'
import { HeaderColors } from '../../../components/types'
import {
  journalItems,
  journalItemsVariables,
} from '../../../__generated__/journalItems'
import { getJournalSections } from '../../../lib/getJournalSections'
import { SortOrder } from '../../../__generated__/globalTypes'

interface IJournalDetail extends AppProps {
  post: journalItem_element
  section: string
  code: string
  today: string
}

const JournalDetail: NextPage<IJournalDetail> = ({
  post,
  section,
  code,
  today,
}) => {
  return (
    <ArticleProvider section={section} code={code} today={today}>
      <JournalDetailPost post={post} />
    </ArticleProvider>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const today = new Date().toISOString()
  const apolloClient = initializeApollo()
  await globalCache(apolloClient)
  const [data] = await Promise.all([
    apolloClient.query<journalItem, journalItemVariables>({
      query: JOURNAL_ITEM,
      variables: {
        where: {
          iblock: { code: { equals: 'articles' } },
          OR: [
            {
              active: { equals: false },
              code: { equals: 'for-partners' },
            },
            {
              active: { equals: true },
              activeFrom: { lte: today },
              activeTo: { equals: null },
            },
            {
              active: { equals: true },
              activeFrom: { lte: today },
              activeTo: { gte: today },
            },
            {
              active: { equals: true },
              activeFrom: { equals: null },
              activeTo: { equals: null },
            },
            {
              active: { equals: true },
              activeFrom: { equals: null },
              activeTo: { gte: today },
            },
          ],
          code: {
            equals: params.code as string,
          },
        },
      },
    }),
    apolloClient.query<journalItems, journalItemsVariables>({
      query: JOURNAL_ITEMS,
      variables: {
        where: {
          iblock: {
            code: {
              equals: 'articles',
            },
          },
          active: { equals: true },
          sections: { some: { code: { equals: params.section as string } } },
          OR: [
            {
              activeFrom: { lte: today },
              activeTo: { equals: null },
            },
            {
              activeFrom: { lte: today },
              activeTo: { gte: today },
            },
            {
              activeFrom: { equals: null },
              activeTo: { equals: null },
            },
            {
              activeFrom: { equals: null },
              activeTo: { gte: today },
            },
          ],
        },
        order: { activeFrom: SortOrder.desc },
        first: 4,
      },
    }),
    getJournalSections(apolloClient),
  ])

  if (!data.data?.element) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  const post = data.data?.element

  const seo = {
    title: data?.data?.element?.seo?.elementMetaTitle,
    description: data?.data?.element?.seo?.elementMetaDescription,
    keywords: data?.data?.element?.seo?.elementMetaKeywords,
  }
  return {
    props: {
      __APOLLO_STATE__: apolloClient.cache.extract(),
      post,
      section: params.section,
      code: params.code,
      headerInitColor: HeaderColors.white,
      seo,
      today,
    },
    revalidate: 60,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths: { params: { section: string; path: string[] } }[] = []
  return {
    paths,
    fallback: 'blocking',
  }
}

export default JournalDetail
